import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import Modal from '../../components/common/Modal.js';
import { showModal } from '../../actions/shared/modal';
import { fetchDeliveryDetails, fetchDeliveryDetailsV2 } from '../actions/index.js';
import PincodeMessage from '../components/pincodeMessage.js';
import PincodeForm from '../components/pincodeForm.js';
import EstimatedDeliveryInformation from '../components/eddInfo.js';
import DeliverySLAContainer, {
  B2cSellerDeliverySLA,
  B2cSellerNoLocation
} from '../../components/desktop/DeliverySLA';
import { ModalTypes } from '../../constants/index.js';
import {
  getServiceabilityDetailsDetails,
  getErrorInServiceabilityFetching,
  getIsCheckServiceabilityCalled,
  getProductServiceAbility,
  getB2cNoLocationSellerName
} from '../../refresh/selectors/productpage';
import {
  getReturnCodCommunication,
  getMultiSellerSlaFlag,
  getMultiSellerSlaConfig,
  getB2cSellerConfig,
  getB2cSellerFlag
} from '../../refresh/selectors/featureFlagsSelector';
import { pickSlaDeliveryNote } from '../../refresh/molecules/DeliverySLA/constants';
import { hasServiceableSlaOption } from '../../refresh/molecules/DeliverySLA/mapDeliverySLA';

const EstimatedDeliveryDate = ({
  selectedSizeCode,
  showModal,
  fetchDeliveryDetails,
  fetchSlaDetails,
  fetchProductServiceability,
  edd,
  isMobile = false,
  luxeEventPush,
  newDesignLuxe,
  serviceabilityDetails,
  errorInServiceabilityFetching,
  isCheckServiceabilityCalled,
  returnCodCommunication,
  productServiceAbility,
  productData,
  multiSellerSlaEnabled,
  b2cSellerEnabled,
  b2cNoLocationSellerName = '',
  slaDeliveryNote,
  selectedSizeName,
}) => {
  const { activePincode, eddDetails, isEDDFetching } = edd;
  // Raw v2 SLA options for the selected size+pincode (stored by fetchDeliveryDetailsV2),
  // fed to PincodeForm's "Delivery And Return Details Viewed" event.
  const slaOptions =
    (activePincode && selectedSizeCode && edd?.slaDetails?.[activePincode]?.[selectedSizeCode]?.options) ||
    null;

  let productSpecificEDDDetails = {};
  if (activePincode && selectedSizeCode && eddDetails[activePincode]) {
    productSpecificEDDDetails = eddDetails[activePincode][selectedSizeCode];
  }
  const { servicability } = productSpecificEDDDetails || {};
  // The SLA cards need a pincode to have anything to show, so without one fall
  // back to the legacy flow ("Enter Pin-code To Know Estimated Delivery Date").
  // Same fallback once scopedView resolves with nothing deliverable — there is no
  // seller choice left to offer, so the legacy EDD copy says it better. The
  // options slice stays null while the call is in flight, so the cards keep
  // owning the loading state.
  const slaResolved = Array.isArray(slaOptions) && slaOptions.length > 0;
  const showMultiSellerSla =
    multiSellerSlaEnabled &&
    selectedSizeCode &&
    activePincode &&
    !(slaResolved && !hasServiceableSlaOption(slaOptions));

  // B2C seller wins this slot when its flag is on, multiseller included. It
  // reads the same scopedView response, so it takes the same size / pincode
  // conditions — without them the legacy EDD copy owns the slot.
  const showB2cSellerSla =
    b2cSellerEnabled &&
    selectedSizeCode &&
    activePincode &&
    !(slaResolved && !hasServiceableSlaOption(slaOptions));

  // Multiseller reads the checkDeliveryDetails v2 slice, so a pincode change
  // has to refresh that instead of the legacy call (same split as the msite PDP).
  // PincodeForm chains off the returned promise, so always hand one back —
  // including the raw SLA options so "Delivery And Return Details Viewed" can
  // attach seller_options (props.slaOptions is still stale in the .then).
  const handlePincodeSubmit = pincode =>
    multiSellerSlaEnabled || b2cSellerEnabled
      ? Promise.resolve(fetchSlaDetails(pincode, selectedSizeCode, productData?.code)).then(
          response => {
            const skuList = response?.sku;
          let options = [];
          if (Array.isArray(skuList) && skuList.length) {
            const match = selectedSizeCode
              ? skuList.find(entry => entry && entry[selectedSizeCode])
              : null;
            if (match) {
              options = match[selectedSizeCode] || [];
            } else {
              const first = skuList[0] || {};
              const firstKey = Object.keys(first)[0];
              options = firstKey ? first[firstKey] || [] : [];
            }
          }
          return { pinCode: pincode, servicability: true, slaOptions: options };
        })
      : fetchDeliveryDetails(pincode);

  return (
    <div
      className={`edd ${servicability === false ? ' edd-content-wrapper ' : ''} ${
        isEDDFetching ? ' edd-content-fetching ' : ''
      }${showMultiSellerSla || showB2cSellerSla ? ' edd-multiseller ' : ''}`}
      id="edd"
      role="presentation"
    >
      {showB2cSellerSla ? (
        <B2cSellerDeliverySLA luxe={newDesignLuxe} />
      ) : showMultiSellerSla ? (
        <DeliverySLAContainer
          luxe={newDesignLuxe}
          trackDeliveryDetailsViewed
          productData={productData}
        />
      ) : (
        <>
          <PincodeMessage edd={edd} selectedSizeCode={selectedSizeCode} showModal={showModal} />
          <EstimatedDeliveryInformation selectedSizeCode={selectedSizeCode} edd={edd} returnCodCommunication={returnCodCommunication} serviceabilityDetails={serviceabilityDetails} errorInServiceabilityFetching={errorInServiceabilityFetching} isCheckServiceabilityCalled={isCheckServiceabilityCalled} productServiceAbility={productServiceAbility}/>
          {/* B2C with no pincode: scopedView never runs, so the legacy copy above
              is all the delivery information there is — but the catalogue still
              names a seller for whatever is picked. Empty (and so hidden) once
              there is a location, where the SLA card owns the row. */}
          <B2cSellerNoLocation sellerName={b2cNoLocationSellerName} luxe={newDesignLuxe} />
        </>
      )}
      <Modal
        name={ModalTypes.EDD_PINCODE}
        className={ModalTypes.EDD_PINCODE}
        id={ModalTypes.EDD_PINCODE}
        ariaLabel="Add pincode"
      >
        <PincodeForm
          selectedSizeCode={selectedSizeCode}
          fetchDeliveryDetails={handlePincodeSubmit}
          edd={edd}
          isMobile={isMobile}
          luxeEventPush={luxeEventPush}
          newDesignLuxe={newDesignLuxe}
          fetchProductServiceability={fetchProductServiceability}
          returnCodCommunicationPdpEnabled={returnCodCommunication?.isPdpEnabled}
          productData={productData}
          enableMultiSellerSla={multiSellerSlaEnabled}
          slaOptions={slaOptions}
          slaDeliveryNote={slaDeliveryNote}
          selectedSizeName={selectedSizeName}
          slaDetailsBySku={
            (activePincode && edd?.slaDetails?.[activePincode]) || null
          }
        />
      </Modal>
    </div>
  );
};

const mapStateToProps = state => {
  const {
    product: { selectedSizeCode = '' },
    edd
  } = state;
  const slaOptions =
    (edd?.activePincode &&
      selectedSizeCode &&
      edd?.slaDetails?.[edd.activePincode]?.[selectedSizeCode]?.options) ||
    [];
  return {
    selectedSizeCode,
    edd,
    serviceabilityDetails: getServiceabilityDetailsDetails(state),
    errorInServiceabilityFetching: getErrorInServiceabilityFetching(state),
    isCheckServiceabilityCalled: getIsCheckServiceabilityCalled(state),
    returnCodCommunication: getReturnCodCommunication(state),
    productServiceAbility: getProductServiceAbility(state),
    multiSellerSlaEnabled: getMultiSellerSlaFlag(state),
    b2cSellerEnabled: getB2cSellerFlag(state),
    // Catalogue seller, for the no-pincode case where scopedView never runs.
    // Empty otherwise — the SLA card's own seller row takes over.
    b2cNoLocationSellerName: getB2cNoLocationSellerName(state),
    slaDeliveryNote: pickSlaDeliveryNote(
      // B2C ships its own copy, so the note follows whichever flag is driving
      // the cards.
      getB2cSellerFlag(state) ? getB2cSellerConfig(state) : getMultiSellerSlaConfig(state),
      Array.isArray(slaOptions) ? slaOptions.length : 0
    ),
    selectedSizeName: state?.product?.selectedSizeName,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    showModal: (name, config) => dispatch(showModal(name, config)),
    fetchDeliveryDetails: pincode => dispatch(fetchDeliveryDetails(pincode)),
    // optionCode = the aggregator option code (`code`) for the product on screen.
    fetchSlaDetails: (pincode, sizeCode, optionCode = '') =>
      dispatch(fetchDeliveryDetailsV2(pincode, sizeCode, 1, false, optionCode)),
    fetchProductServiceability: (...args) => {
      import(/* webpackChunkName: "static/chunk/actions" */ '../../refresh/pages/ProductPage/actions')
        .then(module => {
          const { fetchProductServiceability } = module;
          dispatch(fetchProductServiceability(...args)); // new serviceability api
        })
        .catch(error => console.log('err', error));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(EstimatedDeliveryDate);
